// SPDX-License-Identifier: AGPL-3.0-or-later

import {getSafeItem, setSafeItem} from '@app/features/platform/utils/SafeBrowserStorage';
import ThemeLibrary from '@app/features/theme/state/ThemeLibrary';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {makeAutoObservable, runInAction} from 'mobx';

export type ThemeCategory = 'all' | 'dark' | 'neon' | 'minimal' | 'retro' | 'colorful';

export interface DiscoveryTheme {
	readonly id: string;
	readonly name: string;
	readonly description: string;
	readonly author: string;
	readonly category: ThemeCategory;
	readonly previewColors: readonly [string, string, string, string];
	readonly css: string;
	readonly downloads: number;
	readonly rating: number;
	readonly tags: ReadonlyArray<string>;
	readonly verified: boolean;
	readonly status?: 'approved' | 'pending' | 'rejected';
}

const CURATED_THEMES: ReadonlyArray<DiscoveryTheme> = [];

const STORAGE_CUSTOM_THEMES_KEY = 'flx_theme_discovery_custom';

class ThemeDiscoveryState {
	searchQuery = '';
	selectedCategory: ThemeCategory = 'all';
	customThemes: Array<DiscoveryTheme> = [];

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.loadCustomThemes();
	}

	private loadCustomThemes(): void {
		try {
			const stored = getSafeItem(STORAGE_CUSTOM_THEMES_KEY);
			if (stored) {
				const parsed = JSON.parse(stored) as Array<DiscoveryTheme>;
				if (Array.isArray(parsed)) {
					this.customThemes = parsed;
				}
			}
		} catch {
			// Ignore storage error
		}
	}

	private saveCustomThemes(): void {
		try {
			setSafeItem(STORAGE_CUSTOM_THEMES_KEY, JSON.stringify(this.customThemes));
		} catch {
			// Ignore storage error
		}
	}

	setSearchQuery(query: string): void {
		this.searchQuery = query;
	}

	setSelectedCategory(category: ThemeCategory): void {
		this.selectedCategory = category;
	}

	isThemeApplied(themeId: string): boolean {
		return ThemeLibrary.enabledThemeIds.includes(themeId);
	}

	async applyTheme(theme: DiscoveryTheme): Promise<void> {
		try {
			const existing = ThemeLibrary.themes.find((t) => t.id === theme.id);
			if (!existing) {
				const builtTheme = ThemeLibrary.createThemeFromCss(
					theme.css,
					`${theme.name.toLowerCase().replace(/\s+/g, '-')}.css`,
					'shared_theme',
					{id: theme.id},
				);
				await ThemeLibrary.saveTheme(builtTheme);
			}
			await ThemeLibrary.setThemeEnabled(theme.id, true);
			ToastCommands.createToast({
				type: 'success',
				children: `Theme "${theme.name}" applied successfully!`,
			});
		} catch {
			ToastCommands.createToast({
				type: 'error',
				children: `Failed to apply theme "${theme.name}".`,
			});
		}
	}

	async deactivateTheme(themeId: string): Promise<void> {
		try {
			await ThemeLibrary.setThemeEnabled(themeId, false);
			ToastCommands.createToast({
				type: 'info',
				children: 'Theme deactivated.',
			});
		} catch {
			ToastCommands.createToast({
				type: 'error',
				children: 'Failed to deactivate theme.',
			});
		}
	}

	submitTheme(themeData: Omit<DiscoveryTheme, 'downloads' | 'rating' | 'verified' | 'status'>): DiscoveryTheme {
		const newTheme: DiscoveryTheme = {
			...themeData,
			downloads: 1,
			rating: 5.0,
			verified: false,
			status: 'pending',
		};
		runInAction(() => {
			this.customThemes = [newTheme, ...this.customThemes.filter((t) => t.id !== newTheme.id)];
			this.saveCustomThemes();
		});
		return newTheme;
	}

	approveTheme(themeId: string): void {
		runInAction(() => {
			this.customThemes = this.customThemes.map((t) =>
				t.id === themeId ? {...t, status: 'approved' as const, verified: true} : t,
			);
			this.saveCustomThemes();
		});
	}

	rejectTheme(themeId: string): void {
		runInAction(() => {
			this.customThemes = this.customThemes.map((t) =>
				t.id === themeId ? {...t, status: 'rejected' as const} : t,
			);
			this.saveCustomThemes();
		});
	}

	get allThemes(): Array<DiscoveryTheme> {
		return [...CURATED_THEMES, ...this.customThemes.filter((t) => t.status === 'approved')];
	}

	get pendingThemes(): Array<DiscoveryTheme> {
		return this.customThemes.filter((t) => t.status === 'pending');
	}

	get filteredThemes(): Array<DiscoveryTheme> {
		let list = this.allThemes;

		if (this.selectedCategory !== 'all') {
			list = list.filter((theme) => theme.category === this.selectedCategory);
		}

		const query = this.searchQuery.trim().toLowerCase();
		if (query) {
			list = list.filter(
				(theme) =>
					theme.name.toLowerCase().includes(query) ||
					theme.description.toLowerCase().includes(query) ||
					theme.author.toLowerCase().includes(query) ||
					theme.tags.some((tag) => tag.toLowerCase().includes(query)),
			);
		}

		return list;
	}
}

export default new ThemeDiscoveryState();
